import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface LoginGuardProps {
  children: React.ReactNode;
  message?: string;
  title?: string;
}

const LoginGuard: React.FC<LoginGuardProps> = ({
  children,
  message = "You need to sign in to access this feature.",
  title = "Sign In Required"
}) => {
  const { isLoggedIn, loading } = useAuth();

  // While loading
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-forest-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in - show split layout sign in prompt
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel — Brand (hidden on mobile) */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-forest-400 to-forest-800 relative overflow-hidden">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-white/20 rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full translate-x-1/3 translate-y-1/3" />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          </div>

          <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 w-full">
            {/* Wordmark */}
            <div className="mb-10">
              <h1 className="text-4xl xl:text-5xl font-bold tracking-tight">
                <span className="text-gold-400">Libera</span><span className="text-white">Cure</span>
              </h1>
              <p className="text-forest-200 text-lg mt-2 font-medium">Your Trusted Global Pharmacy</p>
            </div>

            {/* Feature bullets */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Privacy First</p>
                  <p className="text-forest-200 text-sm mt-0.5">Your data is encrypted and never shared with third parties</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Fast Global Shipping</p>
                  <p className="text-forest-200 text-sm mt-0.5">Reliable delivery to your doorstep worldwide</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">24/7 Support</p>
                  <p className="text-forest-200 text-sm mt-0.5">Our team is always here to help you</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel — Login Prompt */}
        <div className="w-full lg:w-1/2 bg-white flex items-center justify-center px-6 sm:px-12">
          <div className="w-full max-w-sm">
            {/* Shield icon */}
            <div className="w-16 h-16 bg-forest-50 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <svg className="w-8 h-8 text-forest-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-neutral-900 text-center mb-2">
              {title}
            </h2>
            <p className="text-neutral-500 text-center text-sm mb-8">
              {message}
            </p>

            <Link
              to="/login"
              className="flex items-center justify-center w-full px-6 py-3.5 text-sm font-semibold rounded-xl text-white bg-forest-400 hover:bg-forest-500 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Sign In
            </Link>

            <div className="text-center mt-5">
              <Link
                to="/"
                className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - show content normally
  return <>{children}</>;
};

export default LoginGuard;
